import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Card,
    CardContent,
    Divider,
    Chip,
    Grid,
    Collapse,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import InfoIcon from '@mui/icons-material/Info';
import PaymentIcon from "@mui/icons-material/Payment";
import DeleteIcon from '@mui/icons-material/Delete';
import { updateUserPlan, deleteUserPlan } from '../api/planApi';
import { formatCurrency } from '../utils/currency';
import { diffDateKeys, localDateToDateKey } from '../utils/affiliateTimezone';

export default function PurchaseHistory({ userId, affiliateId }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [purchases, setPurchases] = useState([]);
    const [openRow, setOpenRow] = useState(null);

    // Edit mode state
    const [editRowId, setEditRowId] = useState(null);
    const [editData, setEditData] = useState({
        sessionsLeft: '',
        startDate: '',
        endDate: '',
        paymentHoliday: false,
    });

    // Delete confirmation modal state
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);

    const token = localStorage.getItem('token');
    // API_URL imported from config
    const role = localStorage.getItem('role');

    const loadPurchases = () => {
        fetch(`${API_URL}/user/user-purchase-history?userId=${userId}&affiliateId=${affiliateId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
        })
            .then((res) => res.json())
            .then((data) => {
                if (Array.isArray(data)) {
                    setPurchases(data);
                } else {
                    setPurchases([]);
                }
            })
            .catch((err) => {
                console.error('Error loading purchase history:', err);
                setPurchases([]);
            });
    };

    useEffect(() => {
        loadPurchases();
    }, [token, userId, affiliateId]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Intl.DateTimeFormat('et-EE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(new Date(dateString));
    };

    const toDateInputValue = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const planHasContract = (plan) => plan?.contractId != null && Number(plan.contractId) > 0;

    // Row click handler for expansion
    const handleRowClick = (id) => {
        if (openRow === id) {
            setOpenRow(null);
        } else {
            setOpenRow(id);
        }
    };

    // Parse training types from JSON string
    const parseTrainingTypes = (trainingTypeString) => {
        try {
            return JSON.parse(trainingTypeString);
        } catch (e) {
            return [];
        }
    };

    const getPlanValidityText = (purchase) => {
        if (purchase.singleCalendarDayValidity) {
            return "1 calendar day";
        }

        if (purchase.exactMonthValidity) {
            return "1 month";
        }
        return `${purchase.validityDays} days`;
    };

    const formatPurchasePrice = (price, purchase) => formatCurrency(price, purchase?.paymentCurrency || "EUR");

    // Status uses the calendar date only. A plan ending today stays active until the day is over.
    const calculateStatus = (purchase) => {
        if (purchase.sessionsLeft === 0) {
            return { label: "Expired", color: "error" };
        }

        const todayKey = localDateToDateKey(new Date());
        const endDateKey = localDateToDateKey(purchase.endDate);
        if (!endDateKey || !todayKey || endDateKey < todayKey) {
            return { label: "Expired", color: "error" };
        }

        const diffDays = diffDateKeys(todayKey, endDateKey);
        if (diffDays != null && diffDays <= 7) {
            return { label: "Expiring Soon", color: "warning" };
        }

        return { label: "Active", color: "success" };
    };

    // Edit mode handlers
    const handleEdit = (e, row) => {
        e.stopPropagation(); // Prevent row expansion when clicking edit
        setEditRowId(row.id);
        setEditData({
            sessionsLeft: row.sessionsLeft || '',
            startDate: toDateInputValue(row.purchasedAt),
            endDate: row.endDate ? new Date(row.endDate).toISOString().split('T')[0] : '',
            paymentHoliday: row.paymentHoliday || false,
        });
    };

    const handleChange = (field, value) => {
        setEditData({ ...editData, [field]: value });
    };

    const handleSaveEdit = async (e) => {
        e.stopPropagation(); // Prevent row expansion when clicking save

        const purchase = purchases.find((item) => item.id === editRowId);
        const body = {
            sessionsLeft: Number(editData.sessionsLeft),
            endDate: editData.endDate ? new Date(editData.endDate) : null,
            paymentHoliday: editData.paymentHoliday,
        };

        if (!planHasContract(purchase) && editData.startDate) {
            body.purchasedAt = new Date(editData.startDate);
        }

        try {
            await updateUserPlan(editRowId, body);
            setEditRowId(null);
            loadPurchases(); // Reload data after successful edit
        } catch (error) {
            console.error('Error updating plan:', error);
        }
    };

    const handleCancelEdit = (e) => {
        e.stopPropagation(); // Prevent row expansion when clicking cancel
        setEditRowId(null);
    };

    // Delete handlers
    const handleDelete = (e, plan) => {
        e.stopPropagation(); // Prevent row expansion when clicking delete
        setPlanToDelete(plan);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (planToDelete) {
            try {
                await deleteUserPlan(planToDelete.id);
                setDeleteConfirmOpen(false);
                setPlanToDelete(null);
                loadPurchases(); // Reload data after deletion
            } catch (error) {
                console.error('Error deleting plan:', error);
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setPlanToDelete(null);
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ ml: 2 }}>
                Purchase History
            </Typography>
            <Paper sx={{ overflowX: 'auto' }}>
                {purchases.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body1">No purchase history available.</Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>Plan Name</TableCell>
                                <TableCell>Purchase Date</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Status</TableCell>
                                {role === 'affiliate' && <TableCell>Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {purchases.map((purchase) => {
                                const isOpen = openRow === purchase.id;
                                const isEditing = editRowId === purchase.id;
                                const trainingTypes = parseTrainingTypes(purchase.trainingType);
                                const status = calculateStatus(purchase);

                                return (
                                    <React.Fragment key={purchase.id}>
                                        {/* Main Row */}
                                        <TableRow
                                            hover
                                            onClick={() => !isEditing && handleRowClick(purchase.id)}
                                            sx={{ cursor: isEditing ? 'default' : 'pointer' }}
                                        >
                                            <TableCell>{purchase.planName}</TableCell>
                                            <TableCell>{formatDate(purchase.purchasedAt)}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{formatPurchasePrice(purchase.price, purchase)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={status.label}
                                                    color={status.color}
                                                    size="small"
                                                />
                                            </TableCell>
                                            {role === 'affiliate' && (
                                                <TableCell>
                                                    {isEditing ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Editing below...
                                                        </Typography>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={(e) => handleEdit(e, purchase)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: 'error.main' }}
                                                                onClick={(e) => handleDelete(e, purchase)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>

                                        {/* Edit Row - Mobile responsive edit fields */}
                                        {isEditing && (
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={role === 'affiliate' ? 5 : 4}>
                                                    <Box sx={{
                                                        margin: { xs: 1, sm: 2 },
                                                        width: '100%',
                                                        maxWidth: '100%',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                            Edit Purchase Details
                                                        </Typography>

                                                        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ width: '100%' }}>
                                                            {!planHasContract(purchase) && (
                                                                <Grid item xs={12} sm={purchase.paymentHoliday ? 12 : 6}>
                                                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                        Plan Start Date
                                                                    </Typography>
                                                                    <TextField
                                                                        type="date"
                                                                        size="small"
                                                                        fullWidth
                                                                        value={editData.startDate}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={(e) => handleChange('startDate', e.target.value)}
                                                                        sx={{ maxWidth: '100%' }}
                                                                    />
                                                                </Grid>
                                                            )}

                                                            <Grid item xs={12} sm={purchase.paymentHoliday ? 12 : 6}>
                                                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                    Plan End Date
                                                                </Typography>
                                                                <TextField
                                                                    type="date"
                                                                    size="small"
                                                                    fullWidth
                                                                    value={editData.endDate}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => handleChange('endDate', e.target.value)}
                                                                    sx={{ maxWidth: '100%' }}
                                                                />
                                                            </Grid>

                                                            <Grid item xs={12} sm={purchase.paymentHoliday ? 12 : 6}>
                                                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                    Remaining Sessions
                                                                </Typography>
                                                                <TextField
                                                                    type="number"
                                                                    size="small"
                                                                    fullWidth
                                                                    value={editData.sessionsLeft}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => handleChange('sessionsLeft', e.target.value)}
                                                                    sx={{ maxWidth: '100%' }}
                                                                />
                                                            </Grid>

                                                            {purchase.paymentHoliday !== undefined && (
                                                                <Grid item xs={12}>
                                                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                                                        Payment Holiday
                                                                    </Typography>
                                                                    <FormControl size="small" sx={{ minWidth: 120, maxWidth: '100%' }}>
                                                                        <Select
                                                                            value={editData.paymentHoliday}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            onChange={(e) => handleChange('paymentHoliday', e.target.value)}
                                                                        >
                                                                            <MenuItem value={false}>No</MenuItem>
                                                                            <MenuItem value={true}>Yes</MenuItem>
                                                                        </Select>
                                                                    </FormControl>
                                                                </Grid>
                                                            )}

                                                            <Grid item xs={12}>
                                                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="primary"
                                                                        onClick={handleSaveEdit}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={handleCancelEdit}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {/* Detail Row */}
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={role === 'affiliate' ? 5 : 4}>
                                                <Collapse in={isOpen && !isEditing} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2 }}>
                                                        <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 3 }}>
                                                            Purchase Details
                                                        </Typography>

                                                        <Grid container spacing={3}>
                                                            {/* Purchase Overview Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card elevation={1} sx={{ height: '100%' }}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <ShoppingBagIcon sx={{ mr: 1 }} />
                                                                            Purchase Information
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            {purchase.affiliateName && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Affiliate Name:
                                                                                    </Typography>
                                                                                    <Typography variant="body1">
                                                                                        {purchase.affiliateName}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Plan Name:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {purchase.planName}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Purchase ID:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {purchase.id}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Starting Date:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {formatDate(purchase.purchasedAt)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Validity Period:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {getPlanValidityText(purchase)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Expiration Date:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {formatDate(purchase.endDate)}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Payment Information Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card elevation={1} sx={{ height: '100%' }}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <PaymentIcon sx={{ mr: 1 }} />
                                                                            Payment Information
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Price:
                                                                                </Typography>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                                    {formatPurchasePrice(purchase.price, purchase)}
                                                                                </Typography>
                                                                            </Box>

                                                                            {purchase.stebbyTicketCode && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Stebby Ticket code:
                                                                                    </Typography>
                                                                                    <Typography variant="body1">
                                                                                        {purchase.stebbyTicketCode}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Sessions:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {purchase.sessionsLeft === 9999 ? "Unlimited" : purchase.sessionsLeft}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Status:
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={status.label}
                                                                                    color={status.color}
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Plan Details Card */}
                                                            <Grid item xs={12}>
                                                                <Card elevation={1}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <InfoIcon sx={{ mr: 1 }} />
                                                                            Plan Details
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Grid container spacing={2}>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Training Types:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                        {trainingTypes.map((type, index) => (
                                                                                            <Chip
                                                                                                key={index}
                                                                                                label={type}
                                                                                                size="small"
                                                                                                color="primary"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete the plan "{planToDelete?.planName}"?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}